import {
    ActionMenu,
    Card,
    Colors,
    DATE_FORMATS,
    FOCUS_RING,
    formatDate,
    Icon,
    Pills,
    Text,
} from '@amelio/ui-library';

import { EmployeeIdentity } from './EmployeeIdentity';
import { MEETING_CARD_COLUMN } from './meetingStyles';

import type { ThematicMeetingListItem } from '@/types/thematic-meetings';
import type { IconProps, OptionProps } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import { getMeetingStatusPill } from '@/pages/ThematicMeetings/utils/meetingSchedule';
import { toDateInputLang } from '@/utils/dateUtils';
import { getFullName } from '@/utils/user';

interface MeetingCardProps {
    meeting: ThematicMeetingListItem;
    title: string;
    onView: () => void;
    onMarkCompleted: () => void;
    onReopen: () => void;
    onDelete: () => void;
}

const VIEW_ACTION = 'view';
const COMPLETE_ACTION = 'complete';
const REOPEN_ACTION = 'reopen';
const DELETE_ACTION = 'delete';

/** A menu entry's leading icon. `inherit` takes the entry's own text colour, so the delete icon
 *  turns red with its label instead of carrying a second, separately chosen red. */
const MenuIcon = ({ name }: { name: IconProps['name'] }) => (
    <Icon
        name={name}
        size={'sm'}
        color={'inherit'}
        ariaHidden
    />
);

export const MeetingCard = ({
    meeting,
    title,
    onView,
    onMarkCompleted,
    onReopen,
    onDelete,
}: MeetingCardProps) => {
    const { t } = useAmelioTranslations();
    // `fr_FR`, the language a French-from-France company reads in, is not a locale the library's
    // date helpers know: left as it is, the date comes back in English.
    const dateLanguage = toDateInputLang(useSelectedAppLanguage());

    const isCompleted = meeting.status === 'Completed';
    // The pill alone says "this meeting is late": a red date beside it would say it twice.
    const statusPill = getMeetingStatusPill(meeting);
    const employeeName = getFullName(meeting.employee ?? undefined);
    // De-emphasis for a completed card comes from the card's own translucent background
    // (bg-white/55) and the avatar's opacity, not from a lighter text colour: a paler token here
    // dropped the contrast ratio below WCAG AA (4.5:1) against that background.
    const mutedTextColor = Colors.BASE_500;
    // The card's own background, border and title colour all flip together on completion — one
    // derived object instead of three separate `isCompleted` ternaries scattered across the JSX.
    // A completed card is outlined, a planned one has no border at all (`variant='flat'`): the
    // design gives a resting card nothing but its own translucent white, and keeps the outline for
    // the one state that has to read as closed.
    const completionStyle = isCompleted
        ? {
              bgColor: 'bg-white/55',
              variant: 'default' as const,
              borderColor: Colors.BASE_200,
              titleColor: mutedTextColor,
          }
        : {
              bgColor: 'bg-white/90',
              variant: 'flat' as const,
              borderColor: undefined,
              titleColor: Colors.BASE_600,
          };

    const actionItems: OptionProps[] = [
        {
            id: VIEW_ACTION,
            label: t('thematicMeetings.list.viewMeeting'),
            startIcon: <MenuIcon name={'lucide-eye'} />,
            dataTestId: 'meeting-action-view',
        },
        meeting.status === 'Planned'
            ? {
                  id: COMPLETE_ACTION,
                  label: t('thematicMeetings.list.markCompleted'),
                  startIcon: <MenuIcon name={'essential-tick-circle'} />,
                  dataTestId: 'meeting-action-complete',
              }
            : {
                  id: REOPEN_ACTION,
                  label: t('thematicMeetings.list.reopenMeeting'),
                  startIcon: <MenuIcon name={'arrow-rotate-left'} />,
                  dataTestId: 'meeting-action-reopen',
              },
        // A completed meeting is read-only until it is reopened (the API refuses the delete with a
        // 409, the same rule as an edit): offering the action here would just be a guaranteed
        // failure, so it only appears once the meeting is planned again.
        ...(isCompleted
            ? []
            : [
                  {
                      id: DELETE_ACTION,
                      label: t('thematicMeetings.list.deleteMeeting'),
                      startIcon: <MenuIcon name={'essential-trash'} />,
                      danger: true,
                      dataTestId: 'meeting-action-delete',
                  },
              ]),
    ];

    const handleActionClick = (itemId: string) => {
        if (itemId === VIEW_ACTION) {
            onView();
        } else if (itemId === COMPLETE_ACTION) {
            onMarkCompleted();
        } else if (itemId === REOPEN_ACTION) {
            onReopen();
        } else if (itemId === DELETE_ACTION) {
            onDelete();
        }
    };

    // A single line under the theme, the date on its own: the time of day, the duration and a
    // preview of the context all belong to the meeting's plan, not to a card whose job is to let
    // the manager scan a list. The date stays long ("12 September 2026") so it reads without
    // decoding a numeric format.
    const scheduleLine = meeting.scheduledAt
        ? formatDate({
              date: meeting.scheduledAt,
              format: DATE_FORMATS.DISPLAY_DATE,
              language: dateLanguage,
          })
        : t('thematicMeetings.plan.notScheduled');

    // The whole card opens the meeting through a "stretched" button covering it at `z-10`; the
    // action menu sits beside it at `z-20`, a sibling rather than a nested control, so each click
    // lands on the topmost element without stopping propagation.
    //
    // Three columns: identity, theme and date, then the status pill and the menu. `flex-wrap`
    // rather than breakpoint variants keeps narrow widths readable (see src/responsive-utilities.css).
    // The first two share `MEETING_CARD_COLUMN` as their basis, which is what makes that wrap
    // happen: a column whose basis is 0 always fits, so the theme would stay on the identity's
    // line and be squeezed to nothing instead of moving under it.
    return (
        <Card
            padding={'none'}
            borderRadius={'lg'}
            bgColor={completionStyle.bgColor}
            variant={completionStyle.variant}
            // No resting shadow: the hover shadow alone tells the pointer the card is clickable.
            shadow={'none'}
            borderColor={completionStyle.borderColor}
            // `relative`: the positioning root of the stretched open-meeting button below.
            className={
                'relative cursor-pointer px-[30px] py-[23px] transition-shadow hover:shadow-md'
            }
        >
            <button
                type={'button'}
                onClick={onView}
                aria-label={t('thematicMeetings.list.openMeeting', { name: employeeName })}
                data-testid={`meeting-card-${meeting.id}`}
                className={`${FOCUS_RING} absolute inset-0 z-10 cursor-pointer rounded-lg`}
            />

            <div className={'flex flex-wrap items-center gap-4'}>
                {/* Non-interactive: the card's single clickable surface is the stretched button
                    above, not this block (see the comment above the `Card`). */}
                <div
                    className={`flex ${MEETING_CARD_COLUMN} min-w-0 flex-shrink-0 items-center gap-3`}
                >
                    <EmployeeIdentity
                        employee={meeting.employee}
                        size={'xsPlus'}
                        nameSize={'default'}
                        muted={isCompleted}
                        avatarSpacing={'roomy'}
                        showJobTitle
                    />
                </div>

                <div className={`flex ${MEETING_CARD_COLUMN} min-w-0 grow flex-col gap-1`}>
                    <div className={'flex items-center gap-2'}>
                        <Icon
                            name={'edit-clipboard-tick'}
                            size={'sm'}
                            color={isCompleted ? Colors.BASE_400 : Colors.PRIMARY}
                            ariaHidden
                        />

                        <Text
                            size={'default'}
                            weight={'medium'}
                            color={completionStyle.titleColor}
                            truncate
                        >
                            {title}
                        </Text>
                    </div>

                    <Text
                        size={'small'}
                        color={mutedTextColor}
                    >
                        {scheduleLine}
                    </Text>
                </div>

                {/* `relative z-20`: sits above the stretched open-meeting button (`z-10`), so the
                    menu (and any future interactive pill here) receives its own clicks directly
                    instead of the card's. */}
                <div className={'relative z-20 flex flex-shrink-0 items-center gap-2'}>
                    {/* One pill, one test id whatever the state it shows: an id that changed with
                        the state would make `meeting-status-tag-…` silently miss the overdue
                        case. */}
                    <Pills
                        variant={statusPill.variant}
                        size={'sm'}
                        dataTestId={`meeting-status-tag-${meeting.id}`}
                    >
                        {t(statusPill.labelKey)}
                    </Pills>

                    {/* `sm`: a 32px trigger, past the 24px minimum target. */}
                    <ActionMenu
                        items={actionItems}
                        onItemClick={handleActionClick}
                        size={'sm'}
                        variant={'subtle'}
                        ariaLabel={t('thematicMeetings.list.meetingActions', {
                            name: employeeName,
                        })}
                        dataTestId={`meeting-action-menu-${meeting.id}`}
                    />
                </div>
            </div>
        </Card>
    );
};
